import { useState, useEffect, useMemo, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Layers,
  Users,
  Save,
  RotateCcw,
  Lock,
  Info,
  CheckCircle2,
} from "lucide-react";

// ───────────────────────────────────────────────────────────────────────────
// Plans SaaS — édition dynamique des plans (features + sièges) depuis l'OPS.
// Contrat backend : trpc.admin.plans.get / trpc.admin.plans.update (AOP-9074).
//   - get()     : lecture ADMIN_OPS+ (consultation).
//   - update()  : écriture SUPER_ADMIN uniquement (propagée à toutes les
//                 entreprises du plan pour les sièges).
// L'édition est verrouillée dans l'UI si le staff n'est pas SUPER_ADMIN.
// ───────────────────────────────────────────────────────────────────────────

// Libellé humain à partir d'une clé de feature (ai_assistant → « Ai assistant »).
function humanizeFeatureKey(key: string): string {
  const cleaned = key.replace(/_/g, " ").trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

// État d'édition local par plan (brouillon avant sauvegarde).
interface PlanDraft {
  seats: string; // saisie contrôlée (string pour l'input)
  features: Set<string>;
}

export default function PlansSaas() {
  const { user } = useAuth();
  const canEdit = user?.staffRole === "SUPER_ADMIN";

  const utils = trpc.useContext();
  const { data, isLoading, isError, error } = trpc.admin.plans.get.useQuery();

  // Brouillons d'édition par planId (init depuis la réponse serveur).
  const [drafts, setDrafts] = useState<Record<string, PlanDraft>>({});
  const [savingPlan, setSavingPlan] = useState<string | null>(null);

  // (Ré)initialise les brouillons quand les données serveur changent.
  useEffect(() => {
    if (!data) return;
    const next: Record<string, PlanDraft> = {};
    for (const plan of data.plans) {
      next[plan.id] = {
        seats: String(plan.includedSeats.value),
        features: new Set(
          plan.features.filter((f) => f.enabled).map((f) => f.featureKey)
        ),
      };
    }
    setDrafts(next);
  }, [data]);

  const updateMutation = trpc.admin.plans.update.useMutation({
    onSuccess: (res) => {
      const label =
        res.propagatedCompanies > 0
          ? `Plan mis à jour — ${res.propagatedCompanies} entreprise(s) resynchronisée(s).`
          : "Plan mis à jour.";
      toast.success(label);
      utils.admin.plans.get.invalidate();
    },
    onError: (err) => {
      toast.error(err.message || "Échec de la mise à jour du plan.");
    },
    onSettled: () => setSavingPlan(null),
  });

  const toggleFeature = useCallback(
    (planId: string, featureKey: string, enabled: boolean) => {
      setDrafts((prev) => {
        const draft = prev[planId];
        if (!draft) return prev;
        const features = new Set(draft.features);
        if (enabled) features.add(featureKey);
        else features.delete(featureKey);
        return { ...prev, [planId]: { ...draft, features } };
      });
    },
    []
  );

  const setSeats = useCallback((planId: string, seats: string) => {
    setDrafts((prev) => {
      const draft = prev[planId];
      if (!draft) return prev;
      return { ...prev, [planId]: { ...draft, seats } };
    });
  }, []);

  const resetPlan = useCallback(
    (planId: string) => {
      const plan = data?.plans.find((p) => p.id === planId);
      if (!plan) return;
      setDrafts((prev) => ({
        ...prev,
        [planId]: {
          seats: String(plan.includedSeats.value),
          features: new Set(
            plan.features.filter((f) => f.enabled).map((f) => f.featureKey)
          ),
        },
      }));
    },
    [data]
  );

  // Détecte les changements non sauvegardés d'un plan vs la réponse serveur.
  const isDirty = useCallback(
    (planId: string): boolean => {
      const plan = data?.plans.find((p) => p.id === planId);
      const draft = drafts[planId];
      if (!plan || !draft) return false;
      const seatsNum = Number(draft.seats);
      if (
        Number.isFinite(seatsNum) &&
        seatsNum !== plan.includedSeats.value
      ) {
        return true;
      }
      const serverEnabled = new Set(
        plan.features.filter((f) => f.enabled).map((f) => f.featureKey)
      );
      if (serverEnabled.size !== draft.features.size) return true;
      for (const k of draft.features) {
        if (!serverEnabled.has(k)) return true;
      }
      return false;
    },
    [data, drafts]
  );

  const handleSave = useCallback(
    (planId: string) => {
      const plan = data?.plans.find((p) => p.id === planId);
      const draft = drafts[planId];
      if (!plan || !draft) return;

      const seatsNum = Number(draft.seats);
      if (!Number.isInteger(seatsNum) || seatsNum < 1 || seatsNum > 1000) {
        toast.error("Le nombre de sièges doit être un entier entre 1 et 1000.");
        return;
      }

      const seatsChanged = seatsNum !== plan.includedSeats.value;
      const serverEnabled = new Set(
        plan.features.filter((f) => f.enabled).map((f) => f.featureKey)
      );
      let featuresChanged = serverEnabled.size !== draft.features.size;
      if (!featuresChanged) {
        for (const k of draft.features) {
          if (!serverEnabled.has(k)) {
            featuresChanged = true;
            break;
          }
        }
      }

      if (!seatsChanged && !featuresChanged) return;

      setSavingPlan(planId);
      updateMutation.mutate({
        planId: planId as
          | "FREE"
          | "DECOUVERTE"
          | "ARTISAN"
          | "ENTERPRISE"
          | "PRO_BTP",
        ...(seatsChanged ? { includedSeats: seatsNum } : {}),
        ...(featuresChanged
          ? { featureKeys: Array.from(draft.features) }
          : {}),
      });
    },
    [data, drafts, updateMutation]
  );

  // Nombre de features actives modifiées vs défaut (pour badge résumé plan).
  const overriddenCount = useMemo(() => {
    const map: Record<string, number> = {};
    if (!data) return map;
    for (const plan of data.plans) {
      map[plan.id] = plan.features.filter(
        (f) => f.enabled !== f.isDefault
      ).length;
    }
    return map;
  }, [data]);

  if (isLoading) {
    return (
      <AdminLayout>
        <ListPageSkeleton />
      </AdminLayout>
    );
  }

  if (isError || !data) {
    return (
      <AdminLayout>
        <div className="p-6">
          <Card>
            <CardContent className="p-6 text-center text-muted-foreground">
              Impossible de charger la configuration des plans.
              {error?.message ? (
                <div className="mt-2 text-sm">{error.message}</div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* En-tête */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Layers className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Plans SaaS</h1>
              <p className="text-sm text-muted-foreground">
                Éditez les features actives et le nombre de sièges inclus par
                plan. La modification des sièges est propagée automatiquement à
                toutes les entreprises du plan.
              </p>
            </div>
          </div>
          {!canEdit && (
            <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <Lock className="h-4 w-4 shrink-0" />
              <span>
                Consultation uniquement. Seul un <strong>SUPER_ADMIN</strong>{" "}
                peut modifier les plans.
              </span>
            </div>
          )}
        </div>

        {/* Grille des plans */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {data.plans.map((plan) => {
            const draft = drafts[plan.id];
            if (!draft) return null;
            const dirty = isDirty(plan.id);
            const seatsNum = Number(draft.seats);
            const seatsOverridden =
              Number.isFinite(seatsNum) && seatsNum !== plan.includedSeats.default;
            const nbOverridden = overriddenCount[plan.id] ?? 0;
            const isSaving = savingPlan === plan.id;

            return (
              <Card key={plan.id} className="flex flex-col">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col gap-1">
                      <CardTitle className="flex items-center gap-2">
                        {plan.name}
                        <Badge variant="secondary" className="font-mono text-xs">
                          {plan.id}
                        </Badge>
                        {plan.hasOverride && (
                          <Badge
                            variant="outline"
                            className="border-amber-300 bg-amber-50 text-amber-700"
                          >
                            personnalisé
                          </Badge>
                        )}
                      </CardTitle>
                      <div className="text-sm text-muted-foreground">
                        {plan.priceMonthly === 0
                          ? "Gratuit"
                          : `${plan.priceMonthly} € / mois`}
                      </div>
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      <div>
                        {draft.features.size} feature(s) active(s)
                      </div>
                      {nbOverridden > 0 && (
                        <div className="text-amber-600">
                          {nbOverridden} modifiée(s) vs défaut
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="flex flex-col gap-4 flex-1">
                  {/* Sièges inclus */}
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label
                        htmlFor={`seats-${plan.id}`}
                        className="flex items-center gap-1.5 text-sm"
                      >
                        <Users className="h-4 w-4 text-muted-foreground" />
                        Sièges inclus
                      </Label>
                      <Input
                        id={`seats-${plan.id}`}
                        type="number"
                        min={1}
                        max={1000}
                        value={draft.seats}
                        disabled={!canEdit}
                        onChange={(e) => setSeats(plan.id, e.target.value)}
                        className="mt-1 w-32"
                      />
                    </div>
                    <div className="pb-1 text-xs text-muted-foreground">
                      Défaut : {plan.includedSeats.default}
                      {seatsOverridden && (
                        <Badge
                          variant="outline"
                          className="ml-2 border-amber-300 bg-amber-50 text-amber-700"
                        >
                          modifié
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="border-t" />

                  {/* Features */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      <Info className="h-4 w-4 text-muted-foreground" />
                      Features actives
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 max-h-80 overflow-y-auto pr-1">
                      {plan.features.map((f) => {
                        const checked = draft.features.has(f.featureKey);
                        const modified = checked !== f.isDefault;
                        return (
                          <div
                            key={f.featureKey}
                            className="flex items-center justify-between gap-2 py-1"
                          >
                            <span
                              className="flex min-w-0 items-center gap-1.5 text-sm"
                              title={f.featureKey}
                            >
                              <span className="truncate">
                                {humanizeFeatureKey(f.featureKey)}
                              </span>
                              {modified && (
                                <span
                                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                                  title="Modifié vs défaut"
                                />
                              )}
                            </span>
                            <Switch
                              checked={checked}
                              disabled={!canEdit}
                              onCheckedChange={(v) =>
                                toggleFeature(plan.id, f.featureKey, v)
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>

                {/* Actions */}
                {canEdit && (
                  <div className="flex items-center justify-end gap-2 border-t p-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!dirty || isSaving}
                      onClick={() => resetPlan(plan.id)}
                    >
                      <RotateCcw className="mr-1.5 h-4 w-4" />
                      Annuler
                    </Button>
                    <Button
                      size="sm"
                      disabled={!dirty || isSaving}
                      onClick={() => handleSave(plan.id)}
                    >
                      {isSaving ? (
                        <>
                          <div className="mr-1.5 h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                          Enregistrement…
                        </>
                      ) : (
                        <>
                          <Save className="mr-1.5 h-4 w-4" />
                          Enregistrer
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <CheckCircle2 className="h-4 w-4" />
          Les surcharges sont tracées dans le journal d'audit. Un badge «
          personnalisé » indique un plan surchargé vs sa configuration d'origine.
        </div>
      </div>
    </AdminLayout>
  );
}
